
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSecurityMonitoring2 } from '@/context/SecurityMonitoringContext';
import { Shield, AlertTriangle, Lock, Bell, FileText, Key, LogOut, GanttChart, Clock, Fingerprint, ShieldAlert } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/useAuthSession';
import { logSecurityEvent } from '@/utils/securityUtils';
import { terminateOtherSessions } from '@/utils/securityMonitoring';

interface SecuritySettingOption {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
  icon: React.FC<{ className?: string }>;
  premium?: boolean;
}

export function SecuritySettings() {
  const { userId, session } = useAuthSession();
  const { isSecurityEnabled, setSecurityEnabled } = useSecurityMonitoring2();
  
  const [securityOptions, setSecurityOptions] = useState<SecuritySettingOption[]>([
    {
      id: 'activity_monitoring',
      title: 'Suspicious Activity Monitoring',
      description: 'Monitor account for suspicious activities and automatically take protective measures',
      enabled: isSecurityEnabled,
      icon: Shield
    },
    {
      id: 'ip_restrictions',
      title: 'IP Address Restrictions',
      description: 'Limit login attempts from unfamiliar IP addresses',
      enabled: false,
      icon: Lock,
      premium: true
    },
    {
      id: 'security_alerts',
      title: 'Security Alerts',
      description: 'Receive notifications about important security events',
      enabled: true,
      icon: Bell
    },
    {
      id: 'activity_log',
      title: 'Activity Logging',
      description: 'Maintain a detailed log of account activities',
      enabled: true,
      icon: FileText
    },
    {
      id: 'session_timeout',
      title: 'Automatic Session Timeout',
      description: 'Automatically log out after period of inactivity',
      enabled: false,
      icon: GanttChart
    },
    {
      id: 'password_age_policy',
      title: 'Password Age Policy',
      description: 'Force password change at regular intervals for better security',
      enabled: true,
      icon: Clock
    },
    {
      id: 'breach_detection',
      title: 'Password Breach Detection',
      description: 'Check if your password appears in known data breaches',
      enabled: true,
      icon: ShieldAlert
    },
    {
      id: 'device_fingerprinting',
      title: 'Device Fingerprinting',
      description: 'Identify suspicious login attempts from unknown devices',
      enabled: true,
      icon: Fingerprint
    }
  ]);

  const handleToggleOption = (id: string, newValue: boolean) => {
    // Handle specific options
    if (id === 'activity_monitoring') {
      setSecurityEnabled(newValue);
    }
    
    // Update state
    setSecurityOptions(prev => 
      prev.map(option => 
        option.id === id ? { ...option, enabled: newValue } : option
      )
    );
    
    // Log the event
    if (userId) {
      logSecurityEvent(
        userId,
        'security_setting_updated',
        `Security setting "${id}" was ${newValue ? 'enabled' : 'disabled'}`,
        { setting: id, enabled: newValue }
      );
    }
    
    // Show feedback
    toast.success(`${newValue ? 'Enabled' : 'Disabled'} ${id.replace('_', ' ')}`);
  };

  const handleLogoutAllDevices = async () => {
    if (!userId || !session?.access_token) {
      toast.error("You need to be logged in to perform this action");
      return;
    }
    
    try {
      await terminateOtherSessions(userId, session.access_token);
      toast.success("All other devices have been logged out");
    } catch (error) {
      console.error("Error logging out all devices:", error);
      toast.error("Failed to log out all devices");
    }
  };

  const handleSecurityAudit = async () => {
    if (!userId) {
      toast.error("You need to be logged in to perform this action");
      return;
    }
    
    toast.success("Security audit initiated", {
      description: "We'll email you a report when it's complete"
    });
    
    await logSecurityEvent(
      userId,
      'security_audit_requested',
      'User requested a security audit'
    );
  };

  const [passwordAge, setPasswordAge] = useState<number | null>(null);
  const [passwordExpiration, setPasswordExpiration] = useState<number | null>(null);

  // Fetch password policy and age data
  useEffect(() => {
    const fetchPasswordPolicy = async () => {
      if (!userId) return;

      try {
        // Get password policy
        const { data: policy } = await supabase
          .from('password_policies')
          .select('max_age_days')
          .single();

        // Get user's most recent password change (from password_history)
        const { data: passwordHistory } = await supabase
          .from('password_history')
          .select('created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (policy && passwordHistory) {
          const lastChange = new Date(passwordHistory.created_at);
          const daysSinceChange = Math.floor((Date.now() - lastChange.getTime()) / (1000 * 60 * 60 * 24));
          setPasswordAge(daysSinceChange);

          if (policy.max_age_days) {
            const daysRemaining = policy.max_age_days - daysSinceChange;
            setPasswordExpiration(daysRemaining > 0 ? daysRemaining : 0);
          }
        }
      } catch (error) {
        console.error("Error fetching password policy:", error);
      }
    };

    fetchPasswordPolicy();
  }, [userId]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Security Settings</h2>
        <Button 
          variant="outline" 
          className="flex items-center gap-2"
          onClick={handleSecurityAudit}
        >
          <AlertTriangle className="h-4 w-4" />
          Run Security Audit
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {securityOptions.map((option) => (
          <Card key={option.id} className={option.premium ? "border-amber-200" : ""}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex gap-2 items-center">
                  <div className="bg-primary/10 p-2 rounded-md">
                    <option.icon className="h-5 w-5 text-primary" />
                  </div>
                  <CardTitle className="text-base">{option.title}</CardTitle>
                </div>
                {option.premium && (
                  <span className="bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded-full">
                    Premium
                  </span>
                )}
              </div>
              <CardDescription className="pt-1">{option.description}</CardDescription>
            </CardHeader>
            <CardFooter>
              <div className="flex justify-between items-center w-full">
                <span className="text-xs text-muted-foreground">
                  {option.enabled ? 'Enabled' : 'Disabled'}
                </span>
                <Switch 
                  checked={option.enabled}
                  onCheckedChange={(value) => handleToggleOption(option.id, value)}
                  disabled={option.premium}
                />
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>
      
      {passwordAge !== null && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div className="flex gap-2 items-center">
                <div className="bg-blue-200 p-2 rounded-md">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <CardTitle className="text-base">Password Age</CardTitle>
              </div>
            </div>
            <CardDescription className="pt-1">Your password is {passwordAge} days old</CardDescription>
          </CardHeader>
          <CardContent>
            {passwordExpiration !== null && (
              <div className="mt-2">
                {passwordExpiration <= 0 ? (
                  <p className="text-red-500 text-sm font-medium">Your password has expired. Please update it soon.</p>
                ) : (
                  <p className="text-sm">Password expires in {passwordExpiration} days</p>
                )}
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full ${
                      passwordExpiration <= 0 ? 'bg-red-500' : 
                      passwordExpiration < 7 ? 'bg-orange-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.max(0, Math.min(100, (passwordExpiration / 90) * 100))}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full text-sm"
              size="sm"
              variant="outline"
              onClick={() => {
                toast.info("Password change feature coming soon!");
              }}
            >
              Change Password
            </Button>
          </CardFooter>
        </Card>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-md p-6 mt-8">
        <h3 className="text-lg font-medium flex items-center gap-2 mb-3">
          <Key className="h-5 w-5 text-blue-600" />
          Active Sessions
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          If you suspect unauthorized access to your account, you can log out from all devices except this one.
        </p>
        <Button 
          onClick={handleLogoutAllDevices}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
        >
          <LogOut className="h-4 w-4" />
          Log Out From All Other Devices
        </Button>
      </div>
    </div>
  );
}
